import React, { useEffect, useMemo } from "react";
import { Button } from "primereact/button";
import { Dropdown, DropdownChangeEvent } from "primereact/dropdown";
import { FloatLabel } from "primereact/floatlabel";
import { useTenantOptions } from "../../../hooks/useTenantOptions";
import { useTenantScope } from "../../../hooks/useTenantScope";
import { useGetEventTypesQuery } from "../../../store/services/events-types";
import { useGetTenantsQuery } from "../../../store/services/tenants";
import type { AdminRulesFilterState } from "../types";
import {
  allRulesTenantsFilterOption,
  buildRulesTenantOptions,
  findGrupsTenantIdFromList,
  fromRulesFilterDropdownTenantId,
  toRulesFilterDropdownTenantId,
} from "../constants/rulesTenant";

export type RulesHierarchicalFilterState = AdminRulesFilterState;

interface RulesHierarchicalFiltersProps {
  filters: RulesHierarchicalFilterState;
  onChange: (next: RulesHierarchicalFilterState) => void;
}

export const RulesHierarchicalFilters = ({
  filters,
  onChange,
}: RulesHierarchicalFiltersProps) => {
  const { data: eventTypes, isLoading: isLoadingEventTypes } = useGetEventTypesQuery();
  const { data: tenants, isLoading: isLoadingTenants } = useGetTenantsQuery({});
  const tenantScope = useTenantScope();
  const { canSeeAllTenants } = useTenantOptions();

  const tenantOptions = useMemo(
    () =>
      buildRulesTenantOptions(
        Array.isArray(tenants) ? tenants : [],
        canSeeAllTenants,
        allRulesTenantsFilterOption
      ),
    [canSeeAllTenants, tenants]
  );
  const grupsTenantId = useMemo(() => findGrupsTenantIdFromList(tenants), [tenants]);
  const selectedTenantId = toRulesFilterDropdownTenantId(filters.tenantId ?? undefined, grupsTenantId);
  const showTenantFilter = !tenantScope.loading && !tenantScope.hidden && !tenantScope.locked;

  useEffect(() => {
    if (tenantScope.loading || !tenantScope.locked || filters.tenantId == null) {
      return;
    }

    onChange({
      ...filters,
      tenantId: undefined,
    });
  }, [filters, onChange, tenantScope.loading, tenantScope.locked]);

  const subTypeOptions = React.useMemo(() => {
    if (!eventTypes || !filters.eventTypeId) return [];
    const selectedType = eventTypes.find((item) => item.eventTypeId === filters.eventTypeId);
    return selectedType?.subTypes ?? [];
  }, [eventTypes, filters.eventTypeId]);

  const handleTypeChange = ({ value }: DropdownChangeEvent) => {
    onChange({
      ...filters,
      eventTypeId: value ?? undefined,
      eventSubTypeId: undefined,
    });
  };

  const handleSubTypeChange = ({ value }: DropdownChangeEvent) => {
    onChange({
      ...filters,
      eventSubTypeId: value ?? undefined,
    });
  };

  const handleTenantChange = ({ value }: DropdownChangeEvent) => {
    onChange({
      ...filters,
      tenantId: fromRulesFilterDropdownTenantId(value as string | undefined, grupsTenantId),
    });
  };

  const clearFilter = (filter: "type" | "sub" | "tenant") => () => {
    if (filter === "type") {
      onChange({
        ...filters,
        eventTypeId: undefined,
        eventSubTypeId: undefined,
      });
      return;
    }
    if (filter === "sub") {
      onChange({
        ...filters,
        eventSubTypeId: undefined,
      });
      return;
    }
    onChange({
      ...filters,
      tenantId: undefined,
    });
  };

  const clearAll = () => {
    onChange({});
  };

  return (
    <div className="grid mt-3 row-gap-4">
      {showTenantFilter && (
        <div className="col-12 md:col-4">
          <div className="p-inputgroup">
            <FloatLabel className="w-full">
              <Dropdown
                id="rulesFilterTenantId"
                name="rulesFilterTenantId"
                onChange={handleTenantChange}
                value={selectedTenantId}
                optionLabel="provider"
                optionValue="id"
                options={tenantOptions}
                loading={isLoadingTenants}
                className="w-full"
              />
              <label htmlFor="rulesFilterTenantId">Tenant</label>
            </FloatLabel>
            <Button
              icon="pi pi-times"
              severity="danger"
              outlined
              aria-label="Limpiar tenant"
              onClick={clearFilter("tenant")}
            />
          </div>
        </div>
      )}

      <div className="col-12 md:col-4">
        <div className="p-inputgroup">
          <FloatLabel className="w-full">
            <Dropdown
              id="rulesFilterEventTypeId"
              name="rulesFilterEventTypeId"
              onChange={handleTypeChange}
              value={filters.eventTypeId}
              optionLabel="eventTypeName"
              optionValue="eventTypeId"
              options={Array.isArray(eventTypes) ? eventTypes : []}
              loading={isLoadingEventTypes}
              className="w-full"
            />
            <label htmlFor="rulesFilterEventTypeId">Tipo</label>
          </FloatLabel>
          <Button
            icon="pi pi-times"
            severity="danger"
            outlined
            aria-label="Limpiar tipo"
            onClick={clearFilter("type")}
          />
        </div>
      </div>

      <div className="col-12 md:col-4">
        <div className="p-inputgroup">
          <FloatLabel className="w-full">
            <Dropdown
              id="rulesFilterEventSubTypeId"
              name="rulesFilterEventSubTypeId"
              onChange={handleSubTypeChange}
              value={filters.eventSubTypeId}
              optionLabel="eventSubTypeName"
              optionValue="eventSubTypeId"
              options={subTypeOptions}
              emptyMessage="Seleccioná un tipo primero"
              className="w-full"
              disabled={!filters.eventTypeId}
            />
            <label htmlFor="rulesFilterEventSubTypeId">Sub-Tipo</label>
          </FloatLabel>
          <Button
            icon="pi pi-times"
            severity="danger"
            outlined
            aria-label="Limpiar sub-tipo"
            onClick={clearFilter("sub")}
            disabled={!filters.eventSubTypeId}
          />
        </div>
      </div>

      <div className="col-12 flex justify-content-end">
        <Button
          label="Limpiar filtros"
          icon="pi pi-filter-slash"
          severity="secondary"
          onClick={clearAll}
        />
      </div>
    </div>
  );
};
